import { pagesIn } from '@/helpers/helpHub'
import { normalizeText, searchTerms } from '@/helpers/text'
import { HELP_HUB_SEARCH_SOURCE } from '@/constants'

// Keys whose strings are identifiers, addresses or files rather than words a dealer reads, at any depth.
const MACHINE_KEYS = new Set([
  'id', 'key', 'slug', 'route', 'href', 'url', 'icon', 'image', 'images', 'video', 'file', 'poster', 'formats',
  'mime', 'ext', 'hash', 'provider', 'name', 'page', 'tasks', 'status', 'source', 'date', 'locale',
])
const isMachineKey = (key) => MACHINE_KEYS.has(key) || /(Slug|Icon|Image|Route|Url|At)$/.test(key)

// Every string a dealer could read in `value`, rows and sections included; a nested help page is left out, it is an entry of its own.
export const readableStrings = (value, into = []) => {
  if (typeof value === 'string') into.push(value)
  else if (Array.isArray(value)) value.forEach((item) => readableStrings(item, into))
  else if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, item]) => { if (!isMachineKey(key)) readableStrings(item, into) })
  }

  return into
}

// The Getting Started steps and every help page nested under them, each once, in reading order; a locked step's page opens too.
export const getStartedEntries = (steps) => {
  const entries = new Map()

  const add = (slug, title, description, page) => {
    if (!slug || entries.has(slug)) return

    entries.set(slug, { key: slug, source: HELP_HUB_SEARCH_SOURCE.GET_STARTED, slug, title, description: description ?? '', body: readableStrings(page) })
    // A row with a page of its own is a page below this one; the other rows are words on this page.
    pagesIn(page).forEach((item) => {
      if (item.page && item.slug) add(item.slug, item.page.title ?? item.title, item.page.helpDescription ?? item.linkDescription ?? item.description, item.page)
    })
  }

  steps.forEach((step) => add(step.key, step.helpTitle ?? step.title, step.helpDescription ?? step.description, step))

  return [...entries.values()]
}

// A release as an entry: the headline and summary its card shows, and every line of the post behind it.
export const whatsNewEntries = (posts) => posts.map((post) => {
  const [section] = post.attributes.section ?? []

  return {
    key: `post-${post.id}`,
    source: HELP_HUB_SEARCH_SOURCE.WHATS_NEW,
    id: post.id,
    title: section?.title ?? post.attributes.title,
    description: section?.description ?? '',
    body: readableStrings(post.attributes),
  }
})

// The Explore help topics rows and the Help Categories, static like the sections they come from.
export const helpTopicEntries = (topics, categories) => [...topics, ...categories].map((topic) => ({
  key: topic.key ?? topic.href,
  source: HELP_HUB_SEARCH_SOURCE.HELP_TOPICS,
  topic,
  title: topic.title,
  description: topic.description ?? '',
  body: [],
}))

// How close a match is: every word in the title, or in the title and blurb, or somewhere on the page; null when a word is missing.
const rankOf = (entry, terms) => {
  const holds = (text) => terms.every((term) => text.includes(term))
  const title = normalizeText(entry.title)
  const blurb = `${title} ${normalizeText(entry.description)}`

  if (holds(title)) return 0
  if (holds(blurb)) return 1
  if (holds(`${blurb} ${normalizeText(entry.body.join(' '))}`)) return 2

  return null
}

// The entries that hold every word of `query`, the closest matches first and otherwise in the order given; none for an empty query.
export const searchHelp = (query, entries) => {
  const terms = searchTerms(query)
  if (!terms.length) return []

  return entries
    .map((entry, index) => ({ entry, index, rank: rankOf(entry, terms) }))
    .filter((match) => match.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((match) => match.entry)
}
